import { Platform } from 'react-native';
import { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { BottomComponentBar, Button, Icon, PopUpView, SearchPage } from '@gripp/shared-ui';
import { Colors } from '../../themes';
import { SearchPageProps } from './searchPage';

export type SearchWrapperProps = Omit<SearchPageProps, 'footer'> & {
  /** Whether to render as modal (desktop) or page (mobile). If not provided, auto-detects based on Platform.OS */
  mode?: 'modal' | 'page';
  /** Modal visibility (required if mode is 'modal') */
  isVisible?: boolean;
  /** Modal dismiss handler (required if mode is 'modal') */
  onDismiss?: () => void;
  /** Page navigation back handler (required if mode is 'page') */
  onBack?: () => void;
};

export const SearchWrapper = ({
  mode,
  isVisible,
  onDismiss,
  onBack,
  ...searchPageProps
}: SearchWrapperProps) => {
  const { t } = useTranslation();
  const triggerSearchRef = useRef<(() => Promise<void>) | null>(null);
  
  // Auto-detect mode if not provided
  const renderMode = mode || (Platform.OS === 'web' ? 'modal' : 'page');
  
  const handleSearchSubmit = () => {
    if (renderMode === 'modal') {
      onDismiss?.();
    } else {
      onBack?.();
    }
    searchPageProps.onSearchSubmit?.();
  };

  const SearchButton = () => {
    return (
      <Button
        style={styles.searchButton}
        onPress={async () => {
          if (triggerSearchRef.current) {
            await triggerSearchRef.current();
          }
        }}
        state="active"
      >
        {t(searchPageProps.searchButtonKey)}
      </Button>
    );
  };

  const BackButton = () => {
    return (
      <Button
        variant="rounded"
        state="active"
        onPress={renderMode === 'modal' ? onDismiss : onBack}
        style={styles.backButton}
        leftIcon={<Icon name="Chevron Left" size={24} color={Colors.white} />}
      />
    );
  };

  const footer = (
    <BottomComponentBar
      backButton={<BackButton />}
      additionalHeaderBlock={<SearchButton />}
    />
  );

  const searchPage = (
    <SearchPage
      {...searchPageProps}
      onSearchSubmit={handleSearchSubmit}
      onSearchTrigger={(trigger) => {
        triggerSearchRef.current = trigger;
        searchPageProps.onSearchTrigger?.(trigger);
      }}
      footer={footer}
    />
  );

  if (renderMode === 'modal') {
    return (
      <PopUpView
        isVisible={isVisible ?? false}
        onDismiss={onDismiss ?? (() => {})}
        containerStyle={{
          maxWidth: 600,
          maxHeight: 700,
          width: '90%',
          height: '90%',
        }}
      >
        {searchPage}
      </PopUpView>
    );
  }

  // Page mode - render directly
  return searchPage;
};

const styles = StyleSheet.create({
  searchButton: {
    flex: 1,
    height: 56,
    maxHeight: 56,
    marginRight: 16,
  },
  backButton: {
    width: 56,
    height: 56,
    minWidth: 56,
    maxHeight: 56,
    borderRadius: 99,
    marginLeft: 16,
  },
});
